import { useCallback, useEffect, useMemo, useRef } from 'react';

import { TASK_CONST } from '@/constant';
import { useFrameDetailQuery, useFrameNeighborQuery } from '@/hook';
import { useWatch } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useAuthStore, useReviewStore, useTaskFrameStore } from '@/store';
import type { FrameData, Task } from '@/type';
import { convertBoxWithLabel, isEqualBoxList } from '@/util';

const useFrameDetail = ({
  taskID,
  frameSeq,
}: {
  taskID: Task['id'];
  frameSeq: FrameData['frameSeq'];
}) => {
  const isManager = useAuthStore(state => state.isManager);
  const { setReviewBoxListWithIndex } = useReviewStore();
  const { allFrameList, hasFrame, setFrame, setIsFrameDataLoading } =
    useTaskFrameStore();
  const { history, setBoxList } = useLabelingBoxContext();
  const isFetchedRef = useRef(false);

  const frameDetailResult = useFrameDetailQuery({
    frameSeq,
    taskID,
  });
  const frameNeighborResult = useFrameNeighborQuery({
    frameSeq,
    taskID,
  });
  const reviewFrameNeighborResult = useFrameNeighborQuery(
    {
      frameSeq,
      taskID,
      mode: 'Reviewer',
    },
    {
      enabled: isManager(),
    },
  );
  const rejectedFrameNeighborResult = useFrameNeighborQuery({
    frameSeq,
    frameWorkState: TASK_CONST.TASK_FRAME_WORKSTATE.REJECT,
    taskID,
  });

  const errorList = useMemo(() => {
    return [frameDetailResult.error, frameNeighborResult.error].filter(
      (error): error is Error => error instanceof Error,
    );
  }, [frameDetailResult.error, frameNeighborResult.error]);

  const isLoading = useMemo(() => {
    return frameDetailResult.isLoading || frameNeighborResult.isLoading;
  }, [frameDetailResult.isLoading, frameNeighborResult.isLoading]);

  const frameData = useMemo(
    () => (isLoading ? allFrameList[frameSeq - 1] : frameDetailResult.data),
    [isLoading, frameSeq, allFrameList, frameDetailResult.data],
  );

  const refetch = useCallback(async (): Promise<void> => {
    await frameDetailResult.refetch();
  }, [frameDetailResult]);

  const setBoxListByFrameData = useCallback(
    (frameData: FrameData): void => {
      const index = frameData.frameSeq - 1;

      history.getState().pause();

      try {
        const workBoxList = frameData.workData?.map(convertBoxWithLabel);

        const reviewBoxList = frameData.reviewData?.map(convertBoxWithLabel);

        setBoxList(workBoxList, index);
        setReviewBoxListWithIndex(reviewBoxList, index);
      } finally {
        history.getState().resume();
      }
    },
    [history, setBoxList, setReviewBoxListWithIndex],
  );

  useWatch(frameData, (newFrameData, oldFrameData) => {
    const oldFrameSeq = oldFrameData?.frameSeq;
    const newFrameSeq = newFrameData?.frameSeq;

    if (newFrameSeq === oldFrameSeq || !frameData) return;

    setBoxListByFrameData(frameData);
  });

  useEffect(() => {
    const currentFrameData = frameDetailResult.data;

    if (
      isLoading ||
      !currentFrameData ||
      frameSeq !== currentFrameData.frameSeq
    ) {
      isFetchedRef.current = false;
      return;
    }

    setIsFrameDataLoading(false);

    const frameIndex = frameSeq - 1;
    const existingFrame = allFrameList[frameIndex];

    if (
      (existingFrame &&
        currentFrameData &&
        isEqualBoxList(
          existingFrame.workData ?? [],
          currentFrameData.workData ?? [],
        ) &&
        isEqualBoxList(
          existingFrame.reviewData ?? [],
          currentFrameData.reviewData ?? [],
        ) &&
        existingFrame.workState === currentFrameData.workState) ||
      isFetchedRef.current
    ) {
      return;
    }

    setFrame(currentFrameData, frameIndex);
    setBoxListByFrameData(currentFrameData);

    isFetchedRef.current = true;
  }, [
    frameDetailResult.data,
    isLoading,
    frameSeq,
    allFrameList,
    hasFrame,
    setFrame,
    setBoxListByFrameData,
  ]);

  useEffect(() => {
    isFetchedRef.current = false;
    setIsFrameDataLoading(true);
  }, [frameSeq]);

  return {
    frameDetail: {
      ...(frameDetailResult.data ?? ({} as FrameData)),
      nextFrame: frameNeighborResult.data?.next,
      nextReviewFrame: reviewFrameNeighborResult.data?.next,
      nextRejectedFrame: rejectedFrameNeighborResult.data?.next,
      prevFrame: frameNeighborResult.data?.prev,
      prevReviewFrame: reviewFrameNeighborResult.data?.prev,
      prevRejectedFrame: rejectedFrameNeighborResult.data?.prev,
    },
    isLoading,
    errorList,
    refetch,
  };
};

export default useFrameDetail;
